// Request form. Renders the request-type metadata + dynamic custom fields
// defined by the admin's Form Builder. Anonymous users land here with name
// + email capture; logged-in customers see those prefilled. After submit,
// any 'file' field values are uploaded as attachments tied to the new
// ticket — keeps the public submit endpoint JSON-only while still letting
// customers upload screenshots inline with their request.
import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { usePortal } from './PortalLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { CheckCircle } from '@phosphor-icons/react';
import PortalField from '../../components/serviceDesk/formBuilder/PortalField';
import { isFieldVisible } from '../../components/serviceDesk/formBuilder/formConditions';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export default function PortalRequestFormPage() {
  const { rtId, slug } = useParams();
  const navigate = useNavigate();
  const { portal, request_types, customer } = usePortal();
  const rt = useMemo(() => request_types.find(r => r.request_type_id === rtId), [request_types, rtId]);

  const [email, setEmail] = useState(customer?.email || '');
  const [name, setName] = useState(customer?.name || '');
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  if (!rt) return (
    <div className="bg-white border border-zinc-200 rounded-lg p-8 text-center">
      <p className="text-sm text-zinc-500">Request type not found. <Link to={`/help/${slug}`} className="text-blue-600 underline">Go back</Link></p>
    </div>
  );

  const fields = rt.form_fields || [];

  const validateFields = () => {
    for (const f of fields) {
      if (!isFieldVisible(f, formData)) continue; // skip hidden conditional fields
      const v = formData[f.key];
      if (!f.required) continue;
      if (f.type === 'checkbox' ? !v : (v === undefined || v === '' || (Array.isArray(v) && v.length === 0))) {
        toast.error(`${f.label || f.key} is required`);
        return false;
      }
      if (f.type === 'email' && v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v))) {
        toast.error(`${f.label} must be a valid email`);
        return false;
      }
      if (f.type === 'url' && v && !/^https?:\/\//.test(String(v))) {
        toast.error(`${f.label} must start with http:// or https://`);
        return false;
      }
    }
    return true;
  };

  const submit = async () => {
    if (!email.trim()) return toast.error('Email required so we can reply');
    if (!validateFields()) return;
    setSubmitting(true);
    try {
      // Strip the raw File objects out of form_data before sending JSON;
      // we'll upload them as attachments after we get the ticket_key back.
      const fileFields = fields.filter(f => f.type === 'file');
      const cleanFormData = { ...formData };
      const pendingFiles = [];
      for (const f of fileFields) {
        const v = formData[f.key];
        if (v && v._file) {
          pendingFiles.push({ key: f.key, label: f.label, file: v._file });
          cleanFormData[f.key] = { name: v.name, size: v.size, _pending_attachment: true };
        }
      }
      // No hardcoded Summary/Details — the backend derives the ticket title
      // and description from this dynamic form_data + the request-type schema.
      const { data } = await axios.post(`${API}/public/portal/${slug}/tickets`, {
        requester_email: email.trim(),
        requester_name: name.trim(),
        request_type_id: rt.request_type_id,
        form_data: cleanFormData,
      });
      // Best-effort attach uploads. Failures don't block the success state —
      // the ticket exists and the customer can re-upload from the detail view.
      for (const pf of pendingFiles) {
        try {
          const fd = new FormData();
          fd.append('file', pf.file);
          fd.append('requester_email', email.trim());
          await axios.post(`${API}/public/portal/${slug}/tickets/${data.ticket_key}/attachments`, fd, { withCredentials: true });
        } catch (e) { console.warn('[portal] attachment failed for', pf.key, e); }
      }
      setSubmitted(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="bg-white border border-zinc-200 rounded-lg p-10 text-center max-w-lg mx-auto" data-testid="portal-submitted">
        <CheckCircle size={42} weight="fill" className="text-emerald-500 mx-auto mb-3" />
        <h2 className="text-xl font-bold text-zinc-900 font-['Outfit']">Request received</h2>
        <p className="text-sm text-zinc-500 mt-2">We've logged your request as <strong className="text-zinc-900 font-mono">{submitted.ticket_key}</strong>. A confirmation email is on its way.</p>
        <div className="mt-6 flex justify-center gap-2">
          <Button variant="outline" onClick={() => navigate(`/help/${slug}`)}>Back to portal</Button>
          {customer && <Button onClick={() => navigate(`/help/${slug}/requests`)}>View my requests</Button>}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="portal-request-form">
      <button onClick={() => navigate(-1)} className="text-xs text-zinc-500 hover:text-zinc-900" data-testid="portal-form-back">← Back</button>
      <div className="bg-white border border-zinc-200 rounded-lg p-6 mt-3">
        <h1 className="text-xl font-bold text-zinc-900 font-['Outfit']">{rt.name}</h1>
        {rt.description && <p className="text-sm text-zinc-500 mt-1">{rt.description}</p>}

        <div className="space-y-4 mt-6">
          {!customer && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Your name</Label>
                  <Input value={name} onChange={(e)=>setName(e.target.value)} placeholder="Your name" data-testid="portal-form-name" />
                </div>
                <div>
                  <Label className="text-xs">Email *</Label>
                  <Input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="you@company.com"
                    data-testid="portal-form-email" />
                </div>
              </div>
              <p className="text-[11px] text-zinc-500">
                Have an account? <Link to={`/help/${slug}/login`} className="text-blue-600 underline">Log in</Link> to track your requests.
              </p>
            </>
          )}

          {fields.length === 0 ? (
            <p className="text-sm text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md p-3" data-testid="portal-form-no-fields">
              No extra details needed — just submit and we'll get back to you.
            </p>
          ) : (
            fields.filter(f => isFieldVisible(f, formData)).map(f => (
              <PortalField key={f.id || f.key} field={f}
                value={formData[f.key]}
                onChange={(v) => setFormData(d => ({ ...d, [f.key]: v }))} />
            ))
          )}

          <div className="pt-2">
            <Button onClick={submit} disabled={submitting} className="w-full" data-testid="portal-form-submit"
              style={{ background: portal.branding?.theme_color, color: 'white' }}>
              {submitting ? 'Submitting…' : 'Submit request'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
